/**
 * The loop slicer — the web counterpart of `LoopSlicerSheet` in `SequencerView.swift`.
 *
 * Slicing is the answer to the loop-versus-grid problem, not a workaround for it. A loop is a
 * bed: it spans a pattern instead of sitting in one of its cells, so it belongs on a pad in
 * looping playback and *not* on a step. Cutting it at its transients turns one bed into a
 * bank of one-shots, and one-shots are exactly what a step grid is built for. That is the
 * classic sampling workflow, and this sheet is where it lives.
 *
 * Three jobs that only make sense together are done in one place: read the loop's tempo, fit
 * it to the project, and cut it at its hits. Splitting them across three screens is how a
 * two-minute job takes twenty.
 */

import { h, onCleanup } from '../../core/dom';
import { computed, effect, signal } from '../../core/signal';
import {
  chip,
  hairlineDivider,
  kickerLabel,
  neonButton,
  parameterRow,
  toggle,
} from '../components/controls';
import { emptyState, processingOverlay, sheet } from '../components/sheet';
import { waveformView } from '../components/waveform';
import {
  PATTERN_LENGTH_OPTIONS,
  clampSampleEdit,
  loopBarLength,
  loopFitRate,
  makeBank,
  makePattern,
  makeTrack,
  offStep,
  projectLoopAssets,
  type EffectParameter,
  type GridSize,
  type SampleAsset,
  type UUID,
} from '../../model/types';
import {
  analysis,
  bufferForAsset,
  currentBankIndex,
  currentPatternID,
  detectAssetTempo,
  fitAssetToProjectTempo,
  mutate,
  notify,
  project,
  reportFailure,
  revision,
  selectedAssetID,
} from '../../app/state';
import { formatDuration, overlayHost } from '../../app/shell';

/**
 * One full bank of slices, which is also the ceiling `Bank`'s largest grid can show. Cutting
 * a busy break at high sensitivity finds sixty hits; sixty pads nobody can see is not a
 * result, so the run is capped and the sheet says so before the button is pressed.
 */
const MAXIMUM_SLICES = 24;

/** Slices shorter than this are analysis noise rather than hits, and are folded away. */
const MINIMUM_SLICE_SECONDS = 0.01;

const SENSITIVITY: EffectParameter = {
  key: 'slicer.sensitivity',
  name: 'Sensitivity',
  min: 0,
  max: 100,
  defaultValue: 50,
  unit: '%',
  scale: 'linear',
  randomisable: false,
};

/**
 * Opens the slicer, optionally on a particular loop.
 *
 * Called from the library (a loop's own actions), from the sequencer (adding a loop to the
 * grid, and the pattern menu) — anywhere a loop is the thing in hand.
 */
export function presentLoopSlicer(assetID?: UUID): void {
  const open = project.peek();
  if (open === null) return;

  const candidates = projectLoopAssets(open);
  const selectedID = signal<UUID | null>(assetID ?? candidates[0]?.id ?? null);
  const markers = signal<readonly number[]>([]);
  const sensitivity = signal(SENSITIVITY.defaultValue);
  const fitToTempo = signal(true);
  const preservePitch = signal(true);
  const failure = signal('');
  const busy = signal(false);

  /** The live asset, re-read through `revision` so a rename or a detected tempo shows up. */
  const asset = computed<SampleAsset | null>(() => {
    revision.value;
    const id = selectedID.value;
    const live = project.value;
    if (id === null || live === null) return null;
    return live.assets.find((entry) => entry.id === id) ?? null;
  });

  const tempo = computed(() => {
    revision.value;
    return project.value?.tempo ?? 96;
  });

  /** Rate that makes the loop line up with the project. Above 1 the loop plays faster. */
  const fitRate = computed(() => {
    const current = asset.value;
    if (current === null || !fitToTempo.value) return 1;
    return loopFitRate(current, tempo.value);
  });

  const body = h('div', { class: 'slicer' });
  const handle = sheet({
    title: 'Slice a loop',
    kicker: open.name,
    detents: [0.94],
    container: overlayHost(),
    content: body,
    onDismiss: () => stopEverything(),
  });

  if (candidates.length === 0) {
    body.appendChild(
      emptyState({
        symbol: 'waveform.path',
        title: 'No loops in this project',
        message:
          'A loop is a bar or more of music: a drum loop, a break, a vocal take. Import one, or install the Loops pack from the library, and it will show up here ready to chop.',
      }),
    );
    handle.present();
    return;
  }

  // MARK: Picker

  const picker = h('div', { class: 'chip-row scroll-x' });
  for (const candidate of candidates) {
    picker.appendChild(
      chip({
        title: candidate.name,
        selected: computed(() => selectedID.value === candidate.id),
        onPress: () => {
          if (selectedID.peek() === candidate.id) return;
          failure.value = '';
          // The load effect below picks the new loop's own markers up; setting them here as
          // well would race it.
          selectedID.value = candidate.id;
        },
      }).el,
    );
  }

  // MARK: Tempo

  const loopTempoValue = h('span', { class: 'slicer-stat-value numeric' });
  const projectTempoValue = h('span', { class: 'slicer-stat-value numeric' });
  const speedValue = h('span', { class: 'slicer-stat-value numeric' });
  const speedCaption = h('span', { class: 'slicer-stat-caption' });

  const stats = h(
    'div',
    { class: 'slicer-stats' },
    h('div', { class: 'slicer-stat' }, kickerLabel('Loop tempo'), loopTempoValue, h('span', { class: 'slicer-stat-caption' }, 'BPM')),
    h('div', { class: 'slicer-stat' }, kickerLabel('Project'), projectTempoValue, h('span', { class: 'slicer-stat-caption' }, 'BPM')),
    h('div', { class: 'slicer-stat' }, kickerLabel('Speed'), speedValue, speedCaption),
  );

  const findTempo = neonButton({
    title: 'Find the tempo',
    style: 'secondary',
    onPress: () => {
      const current = asset.peek();
      if (current === null) return;
      busy.value = true;
      void detectAssetTempo(current.id).finally(() => {
        busy.value = false;
      });
    },
  });

  const fitToggle = toggle({
    title: 'Fit to the project tempo',
    subtitle: 'Speeds the loop up or slows it down so it sits on the grid.',
    on: fitToTempo,
    onChange: (on) => {
      fitToTempo.value = on;
    },
  });

  const pitchToggle = toggle({
    title: 'Keep the pitch',
    // Scoped exactly as the wave editor's time-stretch toggle is: the live pad path is a
    // varispeed, so pitch is only preserved when the slice is rendered or bounced.
    subtitle: computed(() =>
      preservePitch.value
        ? 'Time-stretches instead of repitching when you render or bounce. Live, the pad still repitches.'
        : 'Repitches like tape. Cheaper, and often what a break wants.',
    ),
    on: preservePitch,
    enabled: fitToTempo,
    onChange: (on) => {
      preservePitch.value = on;
    },
  });

  const fitNow = neonButton({
    title: 'Fit this sound now',
    style: 'secondary',
    onPress: () => {
      const current = asset.peek();
      if (current === null) return;
      fitAssetToProjectTempo(current.id);
    },
  });

  const tempoActions = h('div', { class: 'slicer-actions' });

  // MARK: Waveform

  const view = waveformView({
    interaction: 'scrub',
    allowsZoom: false,
    showsTimeRuler: true,
    tint: 'var(--accent-cyan)',
    label: 'Loop waveform',
  });
  view.el.classList.add('slicer-wave');

  // MARK: Slicing

  const sliceCount = h('span', { class: 'slicer-count numeric' });
  const sensitivityRow = parameterRow({
    descriptor: SENSITIVITY,
    value: sensitivity,
    tint: 'var(--accent-cyan)',
    title: 'Sensitivity',
    onChange: (value) => {
      sensitivity.value = value;
    },
  });

  const findHits = neonButton({
    title: 'Find the hits',
    style: 'secondary',
    onPress: () => void findSlices(),
  });

  const build = neonButton({
    title: 'Make pads and a pattern',
    style: 'primary',
    enabled: computed(() => markers.value.length > 0),
    onPress: () => buildSlices(),
  });

  const capNote = h('p', { class: 'muted slicer-note' });
  const failureNote = h('p', { class: 'slicer-failure', role: 'status' });

  body.append(
    kickerLabel('Loop'),
    picker,
    stats,
    tempoActions,
    hairlineDivider(),
    view.el,
    h('div', { class: 'slicer-slice-head' }, kickerLabel('Sensitivity'), sliceCount),
    sensitivityRow.el,
    h('p', { class: 'muted slicer-note' }, 'Higher finds ghost notes as well as hits.'),
    findHits.el,
    hairlineDivider(),
    build.el,
    capNote,
    failureNote,
  );

  // MARK: Reactions

  // The sheet covers the screen behind it, so analysis progress has to be reported *here* or
  // it would run invisibly. Mounted in the same host as the sheet so it inerts the sheet
  // rather than sitting beside it in the tab order.
  let overlay: ReturnType<typeof processingOverlay> | null = null;
  const stopBusy = effect(() => {
    if (busy.value) {
      overlay ??= processingOverlay({
        title: 'Analysing this loop…',
        message: 'Reading its tempo and its hits.',
        container: overlayHost(),
      });
      overlay.present();
      return;
    }
    overlay?.dismiss();
  });

  const stopTempo = effect(() => {
    const current = asset.value;
    const detected = current?.detectedBPM;
    const known = detected !== undefined && detected > 0;
    loopTempoValue.textContent = known ? detected.toFixed(1) : 'None';
    loopTempoValue.classList.toggle('is-unknown', !known);
    projectTempoValue.textContent = tempo.value.toFixed(1);
    const rate = fitRate.value;
    const unchanged = Math.abs(rate - 1) < 0.001;
    speedValue.textContent = `${rate.toFixed(3)}×`;
    speedValue.classList.toggle('is-fitting', !unchanged);
    speedCaption.textContent = unchanged ? 'unchanged' : rate > 1 ? 'faster' : 'slower';

    tempoActions.replaceChildren();
    // Never silent: a loop with no tempo is offered the analyser, and a loop whose tempo the
    // project does not share is offered the fit. One of the two is always on screen.
    if (known) tempoActions.append(fitToggle.el, pitchToggle.el, fitNow.el);
    else tempoActions.append(findTempo.el);
  });

  const stopMarkers = effect(() => {
    const count = markers.value.length;
    sliceCount.textContent = count === 0 ? 'no slices yet' : `${count} slice${count === 1 ? '' : 's'}`;
    sliceCount.classList.toggle('is-found', count > 0);
    view.setSliceMarkers(markers.value);
    capNote.textContent =
      count > MAXIMUM_SLICES
        ? `Only the first ${MAXIMUM_SLICES} slices get pads. That is one full bank.`
        : '';
    capNote.hidden = count <= MAXIMUM_SLICES;
  });

  const stopFailure = effect(() => {
    failureNote.textContent = failure.value;
    failureNote.hidden = failure.value === '';
  });

  /**
   * Loads the waveform for whichever loop is selected.
   *
   * The only dependency is the *selection*. Reading `asset` here would make this run on every
   * project revision — and the found slices would be thrown away and replaced by the asset's
   * stored markers the moment anything else in the app was edited, including this sheet's own
   * "fit to tempo".
   */
  let loadToken = 0;
  const stopLoad = effect(() => {
    if (selectedID.value === null) return;
    const current = asset.peek();
    if (current === null) return;
    markers.value = current.sliceMarkers;
    const token = ++loadToken;
    void (async (): Promise<void> => {
      const buffer = await bufferForAsset(current);
      // The user moved on while the file was decoding; the newer selection owns the view.
      if (token !== loadToken || buffer === null) return;
      try {
        const peaks = await analysis.peaks(buffer, Math.max(8, Math.round(800 / Math.max(0.1, buffer.duration))));
        if (token === loadToken) view.setPeaks(peaks);
      } catch {
        // A slicer without an envelope still slices; the markers are the working surface.
      }
    })();
  });

  function stopEverything(): void {
    stopBusy();
    stopTempo();
    stopMarkers();
    stopFailure();
    stopLoad();
    overlay?.dismiss();
    view.destroy();
    sensitivityRow.destroy();
    findTempo.destroy();
    fitToggle.destroy();
    pitchToggle.destroy();
    fitNow.destroy();
    findHits.destroy();
    build.destroy();
    handle.destroy();
  }
  onCleanup(body, stopEverything);

  // MARK: Work

  async function findSlices(): Promise<void> {
    const current = asset.peek();
    if (current === null) return;
    const buffer = await bufferForAsset(current);
    if (buffer === null) {
      failure.value = 'That sound could not be loaded from this device.';
      return;
    }
    busy.value = true;
    // `analysis.transients` already degrades to an empty answer when the worker is not
    // available, so the failure this reports is "no hits found", never a broken promise.
    const found = await analysis.transients(buffer, sensitivity.peek() / 100).catch(() => [] as number[]);
    busy.value = false;
    if (found.length === 0) {
      failure.value = 'No clear hits were found. Turn the sensitivity up and try again.';
      return;
    }
    failure.value = '';
    markers.value = found;
  }

  /**
   * Turns the slice bounds into a bank of one-shot pads and a pattern that plays them in the
   * order they came out of the loop.
   *
   * Every pad is `oneShot` with no quantise, which is the whole point: the material has gone
   * from a bed that spans the grid to hits that sit *in* it, so the ordinary track — one hit
   * per lit step, ratchets, probability, the lot — is correct again.
   */
  function buildSlices(): void {
    const current = asset.peek();
    const open2 = project.peek();
    if (current === null || open2 === null) return;

    const duration = Math.max(current.duration, 0.001);
    const rate = fitRate.peek();
    const preserve = preservePitch.peek();
    const projectTempo = open2.tempo;

    // Slice bounds in source seconds. A first transient that is not at zero would throw the
    // head of the loop away, so the run always starts at the top of the file.
    const starts = [...markers.peek()].filter((time) => time >= 0 && time < duration).sort((a, b) => a - b);
    if ((starts[0] ?? 1) > 0.02) starts.unshift(0);
    const capped = starts.slice(0, MAXIMUM_SLICES);

    const bounds: { start: number; end: number }[] = [];
    for (let index = 0; index < capped.length; index++) {
      const start = capped[index]!;
      const end = index + 1 < capped.length ? capped[index + 1]! : duration;
      if (end - start <= MINIMUM_SLICE_SECONDS) continue;
      bounds.push({ start, end });
    }
    if (bounds.length === 0) {
      failure.value = 'Those markers are too close together to make pads from.';
      return;
    }

    // The loop's length in steps, snapped to a pattern length the sequencer offers. The rate
    // is applied first: fitting a 90 BPM loop into a 120 BPM project makes it shorter.
    const stepsPerBeat = 4;
    const beats = (duration / rate) * (projectTempo / 60);
    const rawSteps = Math.max(1, Math.round(beats * stepsPerBeat));
    const patternSteps =
      [...PATTERN_LENGTH_OPTIONS].sort((a, b) => Math.abs(a - rawSteps) - Math.abs(b - rawSteps))[0] ?? 16;

    const grid: GridSize = bounds.length > 16 ? 'g4x6' : bounds.length > 12 ? 'g4x4' : 'g3x4';
    const shortName = current.name.trim() === '' ? 'Loop' : current.name.trim();

    // A bank and a pattern are two different objects in two different rails, and one run of
    // this sheet makes one of each. Giving them the same string made them look like one
    // feature seen twice: the board's kicker is the bank name and the sequencer's kicker is
    // the pattern name, so after a slice both screens read "BOOM BAP 80 SLICES" and switching
    // tabs changed nothing. Each name now says which rail it belongs to, a bank holds pads and
    // a pattern is a beat, and both are shorter than the "Slices" they replace.
    const bank = makeBank(`${shortName} Pads`, { grid, color: 'cyan' });
    const pattern = makePattern(`${shortName} Beat`, { length: patternSteps, stepsPerBeat });
    const bankPads = [...bank.pads].sort((a, b) => a.slot - b.slot);
    let placed = 0;

    bounds.forEach((bound, index) => {
      const pad = bankPads[index];
      if (pad === undefined) return;
      placed++;
      pad.name = `${shortName} ${index + 1}`;
      pad.assetID = current.id;
      // Every slice came out of one sound, so every slice wears one colour. Dealing the
      // palette round the grid produced a rainbow board that said nothing about which pad
      // was playing — the only thing a tint is for here.
      pad.color = bank.color;
      pad.playbackMode = 'oneShot';
      pad.retrigger = 'restart';
      pad.quantize = 'off';

      const edit = structuredClone(current.defaultEdit);
      edit.startTime = bound.start;
      edit.endTime = bound.end;
      edit.playbackRate = rate;
      edit.preservesPitchWhenStretched = preserve;
      // The source loops; a slice of it does not. Left on, every pad in the new bank would
      // repeat its own fragment for ever.
      edit.loopEnabled = false;
      delete edit.loopStart;
      delete edit.loopEnd;
      clampSampleEdit(edit, current.duration);
      pad.edit = edit;

      // Ordinary tracks, deliberately: the material is one-shots now, so nothing here is a
      // loop track and every step behaves the way a step grid has always behaved.
      const track = makeTrack({ padID: pad.id, name: pad.name, length: patternSteps, color: pad.color });
      while (track.steps.length < patternSteps) track.steps.push(offStep());
      // The rate cancels out here: a slice a third of the way through the file is a third of
      // the way through the pattern whatever speed it ends up playing at.
      const position = Math.round((bound.start / duration) * patternSteps);
      const stepIndex = Math.min(Math.max(position, 0), patternSteps - 1);
      const step = track.steps[stepIndex];
      if (step !== undefined) step.isActive = true;
      pattern.tracks.push(track);
    });

    const sliceMarkers = capped;
    mutate('Slice Loop Into Pads', (target) => {
      const live = target.assets.find((entry) => entry.id === current.id);
      if (live !== undefined) live.sliceMarkers = sliceMarkers;
      target.banks.push(bank);
      target.patterns.push(pattern);
    });

    const after = project.peek();
    if (after !== null) {
      currentBankIndex.value = Math.max(0, after.banks.length - 1);
      currentPatternID.value = pattern.id;
    }
    selectedAssetID.value = current.id;

    handle.dismiss();
    notify(
      `Sliced into ${placed} pad${placed === 1 ? '' : 's'}`,
      `New bank “${bank.name}”, new pattern “${pattern.name}”.`,
    );
  }

  handle.present();
}

/**
 * The slicer's entry point when the caller has a list rather than one loop: it opens on the
 * newest loop in the project, which is nearly always the one just imported.
 */
export function presentLoopSlicerForNewest(): void {
  const open = project.peek();
  if (open === null) return;
  const newest = projectLoopAssets(open)[0];
  if (newest === undefined) {
    reportFailure(
      'Nothing to slice',
      new Error('This project has no loops yet. Import a bar or more of audio, or install the Loops pack.'),
    );
    return;
  }
  presentLoopSlicer(newest.id);
}

/** Formats a loop's length for a picker row: "2 bars · 4.0 s". */
export function loopLengthSummary(asset: SampleAsset, tempo: number, beatsPerBar: number): string {
  const bars = loopBarLength(asset, tempo, beatsPerBar);
  const rounded = bars >= 1 ? Math.round(bars * 10) / 10 : Math.round(bars * 100) / 100;
  return `${rounded} bar${rounded === 1 ? '' : 's'} · ${formatDuration(asset.duration)}`;
}
